import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../context/AnalyticsContext';
import AudiLogo from '../components/AudiLogo';

const { FiPhone, FiMail, FiMapPin, FiClock, FiCalendar, FiSend, FiMessageCircle } = FiIcons;

const Contact = () => {
  const { trackEvent } = useAnalytics();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: '',
    preferredContact: 'email',
    vehicleInterest: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackEvent('form_fill', { type: 'contact', subject: formData.subject });
    setFormSubmitted(true);
    // In a real implementation, you'd send the form data to a server here
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'general',
        message: '',
        preferredContact: 'email',
        vehicleInterest: ''
      });
      setFormSubmitted(false);
    }, 5000);
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      details: ['(902) 455-7890', 'Customer Service'],
      action: 'tel:+19024557890'
    },
    {
      icon: FiMail,
      title: 'Email',
      details: ['info@halifaxaudi.ca', 'Customer Support'],
      action: 'mailto:info@halifaxaudi.ca'
    },
    {
      icon: FiMapPin,
      title: 'Address',
      details: ['6077 Columbus St', 'Halifax, NS B3K 0E9'],
      action: 'https://maps.google.com/?q=6077+Columbus+St+Halifax+NS'
    },
    {
      icon: FiClock,
      title: 'Hours',
      details: ['Mon-Thu: 9AM-8PM', 'Fri-Sat: 9AM-6PM', 'Sunday: Closed'],
      action: null
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1600703136783-bdb5ea365239?w=1920&h=600&fit=crop&crop=center" alt="Audi showroom" className="w-full h-full object-cover opacity-60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light mb-6 uppercase tracking-tight">
              Contact Halifax Audi
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto font-light">
              Our team of Audi specialists is ready to assist you with any questions about our vehicles, services, or to schedule a personal consultation.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white border border-gray-200 p-8"
          >
            <h2 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
              Send us a Message
            </h2>

            {formSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiSend} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  Thank You
                </h3>
                <p className="text-gray-600">
                  Your message has been sent successfully. A member of our team will contact you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-2 uppercase tracking-wider">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-600 focus:border-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-2 uppercase tracking-wider">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-600 focus:border-red-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-2 uppercase tracking-wider">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(902) 555-0123"
                      className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-600 focus:border-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-2 uppercase tracking-wider">
                      Subject
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-600 focus:border-red-600"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="test-drive">Schedule Test Drive</option>
                      <option value="financing">Financing Questions</option>
                      <option value="service">Service Appointment</option>
                      <option value="parts">Parts & Accessories</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-2 uppercase tracking-wider">
                    Vehicle of Interest (optional)
                  </label>
                  <input
                    type="text"
                    name="vehicleInterest"
                    value={formData.vehicleInterest}
                    onChange={handleInputChange}
                    placeholder="e.g., 2024 Audi Q5"
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-600 focus:border-red-600"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-2 uppercase tracking-wider">
                    Preferred Contact Method
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="email"
                        checked={formData.preferredContact === 'email'}
                        onChange={handleInputChange}
                        className="text-red-600 focus:ring-red-600"
                      />
                      <span className="ml-2">Email</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="phone"
                        checked={formData.preferredContact === 'phone'}
                        onChange={handleInputChange}
                        className="text-red-600 focus:ring-red-600"
                      />
                      <span className="ml-2">Phone</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="text"
                        checked={formData.preferredContact === 'text'}
                        onChange={handleInputChange}
                        className="text-red-600 focus:ring-red-600"
                      />
                      <span className="ml-2">Text/SMS</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-2 uppercase tracking-wider">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-600 focus:border-red-600"
                    placeholder="How can we assist you?"
                  ></textarea>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full bg-red-600 text-white py-3 px-6 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiSend} className="w-5 h-5" />
                    <span>Send Message</span>
                  </button>
                </div>
              </form>
            )}
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Visit Halifax Audi
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="bg-white border border-gray-200 p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-red-600 p-3 rounded-none">
                        <SafeIcon icon={info.icon} className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-light text-gray-900 mb-2 uppercase tracking-tight">
                          {info.title}
                        </h3>
                        {info.details.map((detail, idx) => (
                          <p
                            key={idx}
                            className={idx === 0 ? 'text-red-600 font-medium' : 'text-gray-600 text-sm'}
                          >
                            {detail}
                          </p>
                        ))}
                        {info.action && (
                          <a
                            href={info.action}
                            target={info.action.includes('maps') ? '_blank' : undefined}
                            rel={info.action.includes('maps') ? 'noopener noreferrer' : undefined}
                            className="inline-flex items-center mt-2 text-gray-600 hover:text-red-600 transition-colors text-sm uppercase tracking-wider"
                          >
                            <span>Contact now</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-gray-200 p-6">
              <h3 className="text-lg font-light text-gray-900 mb-4 uppercase tracking-tight">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <button className="w-full bg-black text-white py-3 px-4 font-medium hover:bg-gray-900 transition-colors flex items-center justify-center space-x-2 uppercase tracking-wider text-sm">
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                  <span>Chat with an Audi Specialist</span>
                </button>
                <button className="w-full bg-red-600 text-white py-3 px-4 font-medium hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 uppercase tracking-wider text-sm">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                  <span>Schedule Test Drive</span>
                </button>
                <a
                  href="tel:+19024557890"
                  className="w-full bg-white border border-black text-black py-3 px-4 font-medium hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2 uppercase tracking-wider text-sm"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  <span>Call (902) 455-7890</span>
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white border border-gray-200 p-6">
              <h3 className="text-lg font-light text-gray-900 mb-4 uppercase tracking-tight">
                Visit Our Halifax Showroom
              </h3>
              <div className="bg-gray-200 h-64 flex items-center justify-center">
                <iframe
                  title="Halifax Audi Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2839.2457060720026!2d-63.60900042326815!3d44.660119971817685!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4b5a2267d42160f1%3A0x9e6a0a7435f2e2e6!2sColumbus%20St%2C%20Halifax%2C%20NS!5e0!3m2!1sen!2sca!4v1706805185257!5m2!1sen!2sca"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                6077 Columbus St, Halifax, NS B3K 0E9
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Departments Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Our Departments
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Contact the specific department to streamline your inquiry and get the fastest assistance possible.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'New Vehicle Sales',
                contact: 'sales@halifaxaudi.ca',
                phone: '(902) 455-7890 ext. 1',
                description: 'Explore our latest Audi models and find the perfect vehicle to match your lifestyle.'
              },
              {
                title: 'Certified Pre-Owned',
                contact: 'cpo@halifaxaudi.ca',
                phone: '(902) 455-7890 ext. 2',
                description: 'Quality pre-owned Audi vehicles that meet our rigorous certification standards.'
              },
              {
                title: 'Service Department',
                contact: 'service@halifaxaudi.ca',
                phone: '(902) 455-7890 ext. 3',
                description: 'Factory-trained technicians providing expert maintenance and repair services.'
              },
              {
                title: 'Parts & Accessories',
                contact: 'parts@halifaxaudi.ca',
                phone: '(902) 455-7890 ext. 4',
                description: 'Genuine Audi parts and accessories to maintain your vehicle performance and appearance.'
              },
              {
                title: 'Finance Department',
                contact: 'finance@halifaxaudi.ca',
                phone: '(902) 455-7890 ext. 5',
                description: 'Flexible financing options and lease programs tailored to your needs.'
              },
              {
                title: 'Customer Relations',
                contact: 'customercare@halifaxaudi.ca',
                phone: '(902) 455-7890 ext. 6',
                description: 'Dedicated to ensuring your complete satisfaction throughout your Audi ownership.'
              }
            ].map((dept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 p-6"
              >
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  {dept.title}
                </h3>
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiMail} className="w-4 h-4 text-red-600" />
                  <a href={`mailto:${dept.contact}`} className="text-gray-600 hover:text-red-600 transition-colors">
                    {dept.contact}
                  </a>
                </div>
                <div className="flex items-center space-x-2 mb-4">
                  <SafeIcon icon={FiPhone} className="w-4 h-4 text-red-600" />
                  <a href={`tel:${dept.phone.replace(/\D/g, '')}`} className="text-gray-600 hover:text-red-600 transition-colors">
                    {dept.phone}
                  </a>
                </div>
                <p className="text-gray-600">{dept.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="mb-6">
              <AudiLogo className="h-12 w-auto mx-auto" />
            </div>
            <h2 className="text-3xl md:text-4xl font-light mb-4 uppercase tracking-tight">
              Experience Audi in Halifax
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto font-light">
              Visit our showroom today to experience the perfect blend of luxury, performance, and technology that defines the Audi brand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+19024557890"
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Call Now
              </a>
              <a
                href="#"
                className="border-2 border-white text-white px-8 py-3 font-medium hover:bg-white hover:text-black transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Get Directions
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;